import { createContext, useContext, useEffect, useState } from "react";
import { apiLogin, apiRegister, apiMe, apiLogout, adminLogin as apiAdminLogin, setToken, clearToken, getToken } from "@/lib/api";
import { AUTH_EXPIRED_EVENT } from "@/lib/session";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined); // undefined = checking, null = guest, object = auth

  useEffect(() => {
    const boot = async () => {
      if (!getToken()) {
        setUser(null);
        return;
      }
      try {
        const me = await apiMe();
        setUser(me);
      } catch {
        clearToken();
        setUser(null);
      }
    };
    boot();
  }, []);

  // The API client clears a rejected token; the context must follow so no stale user stays on screen.
  useEffect(() => {
    const expired = () => setUser(null);
    window.addEventListener(AUTH_EXPIRED_EVENT, expired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, expired);
  }, []);

  const login = async (email, password) => {
    const data = await apiLogin({ email, password });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  // Admin sign-in updates the token AND the context together (no reload needed).
  const adminLogin = async (email, password) => {
    const data = await apiAdminLogin({ email, password });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const data = await apiRegister({ name, email, password });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch {
      // ignore
    }
    clearToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, adminLogin, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
